/**
 * [BaselineBasicInfo] Step 2 of the Baseline module, provides the form section for editing/viewing the basic details
 * of a Baseline record (e.g. name, description, currency, periods).
 * - Uses local state for form fields, syncing with parent (BaselineMain.jsx) via props and callbacks.
 * - Applies standardized field validation via verifyFields().
 *
 * @component
 * @param {Object} props
 * @param {Object} props.item The current baseline record to edit (from Redux or parent).
 * @param {string} [props.mode="edit"] Controls editability of the form.
 * @param {string} [props.size] UI size for text fields/selects.
 * @param {number} [props.formFieldWidth] Width for selects.
 * @param {function(Object):void} props.onLocalBaselineChange Callback to bubble up changed baseline data.
 * @param {function(boolean):void} props.onValidateData Callback to notify parent about validation status.
 * @param {function(boolean):void} props.onSetDataChanged Callback to notify parent when user changes data.
 * @param {function():void} props.onClearComplete Callback to notify when the form has been cleared/reset.
 * @returns {React.ReactElement}
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUpdateEffect } from "react-use";
import validator from "validator";

// Custom hooks
import { useDialogBox } from "src/hooks/useDialogBox";

// Layout/UI
import { NumericFormatAdapter } from "src/components/UI/common/NumericFormatAdapter";
import {
	Box,
	createListCollection,
	Field,
	Icon,
	Input,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectLabel,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Portal,
	Stack,
	MetreaTooltip,
} from "@metrea-ui/v2";
import FormTextField from "src/components/UI/common/FormTextField";

// import styles from "./BaselineBasicInfo.module.css";
import { InfoRegular } from "@fluentui/react-icons";

// Utilities
import { Session } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";
import { verifyFields } from "src/utils/formValidationUtils";
import { mColor } from "src/utils/themeUtils";

// Constants
import { arrayCurrency, arrayCurrencyUnit } from "./BaselineVariables";

const getSelectLabel = (item) =>
	item?.label ??
	item?.extended_name ??
	item?.display_name ??
	item?.name ??
	item?.comp_value ??
	(item?.code && item?.symbol ? `${item.code} - ${item.symbol}` : undefined) ??
	item?.code ??
	item?.title ??
	item?.text ??
	String(item?.id ?? "");

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const buildSelectCollectionItems = (items = []) =>
	items.map((item) => ({
		label: getSelectLabel(item),
		value: String(item?.id ?? item?.value ?? ""),
		rawValue: item?.id ?? item?.value ?? "",
	}));

const sizeToFieldControl = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const BaselineBasicInfo = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const uiSize = props.size;
	const isDisabled = props.mode !== "edit";
	const onValidateDataRef = useRef(props.onValidateData);

	// --- Local State ---
	// Local copy of the current baseline (driven from props)
	const initialBaselineSafe = props.item && typeof props.item === "object" ? props.item : {};
	const [currBaseline, setCurrBaseline] = useState(initialBaselineSafe);
	const [nameError, setNameError] = useState("");
	const [currencyError, setCurrencyError] = useState("");
	const [currencyUnitError, setCurrencyUnitError] = useState("");
	const [currencyDisplayError, setCurrencyDisplayError] = useState("");
	const [periodNumberError, setPeriodNumberError] = useState("");

	// Custom hooks
	const { withConfirmation } = useDialogBox();

	// Keep the shared field validators stable so the parent validation effect
	// runs for data changes instead of every render.
	const validators = useMemo(
		() => [
			{
				field: "name",
				validate: (v) => v !== "",
				setError: setNameError,
				message: "Name is required!",
			},
			{
				field: "currency_id",
				validate: (v) => v !== "" && v !== "0",
				setError: setCurrencyError,
				message: "Currency Symbol is required!",
			},
			{
				field: "currency_unit",
				validate: (v) => v !== "",
				setError: setCurrencyUnitError,
				message: "Currency Unit is required!",
			},
			{
				field: "currency_display_decimal",
				validate: (v) => v !== "" && validator.isInt(v, { min: 0, max: 6 }),
				setError: setCurrencyDisplayError,
				message: "Currency Display Preference is required, and it must be an integer between 0 and 6.",
			},
			{
				field: "number_of_period",
				validate: (v) => v !== "" && validator.isInt(v, { min: 1, max: 10 }),
				setError: setPeriodNumberError,
				message: "Number of Period is required, and it must be an integer between 1 and 10.",
			},
		],
		[]
	);
	const verifyDataFields = useCallback((data) => verifyFields(data, validators), [validators]);

	useEffect(() => {
		debugLog(2, "[BaselineBasicInfo][FLOW] mounted", {});
	}, []);

	// Parent handlers are recreated during parent renders; keep the latest callback
	// without treating callback identity churn as a reason to revalidate unchanged data.
	useEffect(() => {
		onValidateDataRef.current = props.onValidateData;
	}, [props.onValidateData]);

	// --- Effect: sync local when parent item changes
	useUpdateEffect(() => {
		// defensive null checks
		if (props.item && typeof props.item === "object") {
			setCurrBaseline(props.item);
		} else {
			setCurrBaseline({});
		}
		debugLog(3, "[BaselineBasicInfo][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	// Bubble up the latest validation result for parent save/navigation guards.
	useEffect(() => {
		const hasError = verifyDataFields(currBaseline);
		onValidateDataRef.current(hasError);

		debugLog(3, "[BaselineBasicInfo][STATE] validation status updated", {
			currBaseline: currBaseline,
			hasError: hasError,
		});
	}, [currBaseline, verifyDataFields]);

	// Shows a warning dialog if # of periods is decreased (potential data loss).
	const verifyInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(3, "[BaselineBasicInfo][ACTION] verifyInputChange", { name, value });
		if (name === "number_of_period") {
			const newValue = parseInt(value);
			if (newValue < currBaseline.number_of_period_prev) {
				withConfirmation(
					{
						title: "Warning",
						message: [
							"You'd changed the Number of Period(s).",
							"Some data will be deleted if you choose to continue and save.",
							" ",
							"Do you want to proceed?",
						],
					},
					() => {
						debugLog(3, "[BaselineBasicInfo][ACTION] verifyInputChange.confirm", {
							currBaseline: currBaseline,
						});
					},
					() => {
						const revertedData = {
							...currBaseline,
							number_of_period: currBaseline.number_of_period_prev,
						};
						setCurrBaseline(revertedData);
						props.onLocalBaselineChange(revertedData);
						debugLog(3, "[BaselineBasicInfo][ACTION] verifyInputChange.cancel", {
							currBaseline: currBaseline,
						});
					},
				);
			}
		}
	};

	// --- Handlers ---
	// Handles changes for the local text and numeric field inputs on this screen.
	const handleInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(7, "[BaselineBasicInfo][ACTION] handleInputChange", { name, value });

		// Update local state and alert parent of changes
		const updateData = {
			...currBaseline,
			[name]: value,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		// Could be noisy to log every keystroke,
		// so only log when there's an error or when the field is number_of_period (which has the potential for data loss and is important to verify in logs)
		// debugLog(4, "[BaselineBasicInfo] handleInputChange", {
		// 	name: name,
		// 	value: value,
		// 	updateData: updateData,
		// });

		setCurrBaseline(updateData);
		props.onSetDataChanged(true);

		// Notify parent component of changes.
		props.onLocalBaselineChange(updateData);

		// props.onClearComplete();
	};

	// For dropdowns/selectors - Handles value changes for all dropdowns/selectors.
	const changeValueHandler = (name, selected) => {
		const updateData = {
			...currBaseline,
			[name]: selected,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		if (selected !== "") {
			if (name === "currency_id") {
				// Robust lookup: match by id (string/number), fallback by code
				const curr =
					arrayCurrency?.find((c) => String(c.id) === String(selected)) ||
					arrayCurrency?.find((c) => String(c.code) === String(selected)) ||
					null;

				if (curr) {
					updateData.currency_code = curr.code ?? "";
					updateData.currency_symbol = curr.symbol ?? "";
					setCurrencyError("");
				} else {
					// Guard against mismatched ids to avoid crashes
					setCurrencyError("Invalid currency.");
					updateData.currency_code = updateData.currency_code ?? "";
					updateData.currency_symbol = updateData.currency_symbol ?? "";
					debugLog(3, "[BaselineBasicInfo][ACTION] changeValueHandler.currencyNotFound", {
						selected,
						arrayCurrencySample: arrayCurrency?.slice?.(0, 3),
					});
				}
			} else if (name === "currency_unit") setCurrencyUnitError("");
		} else {
			if (name === "currency_id") {
				setCurrencyError("Currency Symbol is required!");
				updateData.currency_code = "";
				updateData.currency_symbol = "";
			} else if (name === "currency_unit") {
				setCurrencyUnitError("Currency Unit is required!");
			}
		}

		debugLog(3, "[BaselineBasicInfo][ACTION] changeValueHandler", {
			name: name,
			selected: selected,
			updateData: updateData,
		});

		setCurrBaseline(updateData);
		props.onSetDataChanged(true);
		props.onLocalBaselineChange(updateData);

		// props.onClearComplete();
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineBasicInfo][FLOW] render", {
		renderCount: renderCount.current,
		currBaseline: currBaseline,
	});
	debugLog(9, "[BaselineBasicInfo][FLOW] renderState", {
		props: props,
		currBaseline: currBaseline,
		nameError: nameError,
		currencyError: currencyError,
		currencyUnitError: currencyUnitError,
		currencyDisplayError: currencyDisplayError,
		periodNumberError: periodNumberError,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	// Field helpers keep the error messages easy to reuse across the direct v2-native selects.
	const nameProps = nameError ? { error: true, helperText: nameError } : {};
	const currencyProps = currencyError ? { error: true, helperText: currencyError } : {};
	const currencyUnitProps = currencyUnitError ? { error: true, helperText: currencyUnitError } : {};
	const currencyDisplayProps = currencyDisplayError
		? { error: true, helperText: currencyDisplayError }
		: {};
	const periodNumberProps = periodNumberError ? { error: true, helperText: periodNumberError } : {};
	const currencyCollection = useMemo(
		() =>
			createListCollection({
				// Intentionally keep the direct v2 menu to real lookup values only.
				// This Baseline screen does not need an explicit clear-to-empty option in the dropdown.
				items: buildSelectCollectionItems(arrayCurrency),
			}),
		[],
	);
	const currencyUnitCollection = useMemo(
		() =>
			createListCollection({
				// Match the currency symbol select: keep the menu to real unit choices only
				// instead of reintroducing an empty option just to trigger required-state validation.
				items: buildSelectCollectionItems(arrayCurrencyUnit),
			}),
		[],
	);

	// Plain text/textarea fields now share the stable direct-v2 helper, while
	// numeric/select variants stay local to preserve their screen-specific rules.
	const renderTextField = (props) => (
		<FormTextField {...props} size={sizeToFieldControl(uiSize)} helperColor={mColor.Danger} />
	);

	const renderSingleSelectField = ({
		id,
		name,
		label,
		collection,
		selectedItem,
		placeholder,
		errorText,
		required = false,
		disabled = false,
	}) => {
		const invalid = Boolean(errorText);
		const hasHelperContent = Boolean(errorText);
		const helpId = `${id}-helptext`;
		const wantsNumber =
			typeof selectedItem === "number" ||
			collection.items.some((item) => typeof item?.rawValue === "number");
		const currentValue = selectedItem == null ? "" : String(selectedItem);

		return (
			<Box width={props.formFieldWidth || "100%"} mt="1rem" mb="0.5rem">
				<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
					<MetreaSelectRoot
						variant="outline"
						collection={collection}
						size={sizeToSelectSize(uiSize)}
						width="100%"
						name={name}
						value={currentValue ? [currentValue] : []}
						invalid={invalid}
						disabled={disabled}
						onValueChange={(details) => {
							// Preserve the existing field contract by translating the direct Select value
							// back to the same string/number shape that changeValueHandler already expects.
							const raw = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
							const nextValue = raw === "" ? "" : wantsNumber ? Number(raw) : String(raw);
							changeValueHandler(name, nextValue);
						}}
					>
						<MetreaSelectLabel>
							{label}
							{required ? <Field.RequiredIndicator /> : null}
						</MetreaSelectLabel>
						<MetreaSelectTrigger
							id={id}
							disabled={disabled}
							aria-invalid={invalid ? "true" : undefined}
							aria-describedby={helpId}
						>
							<MetreaSelectValueText placeholder={placeholder} />
						</MetreaSelectTrigger>
						<Portal>
							<MetreaSelectContent>
								{collection.items.map((item) => (
									<MetreaSelectItem key={item.value} item={item}>
										{item.label}
									</MetreaSelectItem>
								))}
							</MetreaSelectContent>
						</Portal>
					</MetreaSelectRoot>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: invalid ? mColor.Danger : "inherit",
								visibility: hasHelperContent ? "visible" : "hidden",
							}}
						>
							{hasHelperContent ? errorText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};

	// Keep the numeric helper local for now because these fields still carry
	// screen-specific tooltip, validation, and blur-warning behavior.
	const renderNumericField = ({
		id,
		name,
		label,
		value,
		onChange,
		onBlur,
		disabled = false,
		required = false,
		errorText = "",
		width = "100%",
		endAdornment,
		formatSpec,
	}) => {
		const invalid = Boolean(errorText);
		const helpId = `${id}-helptext`;
		const controlSize = sizeToFieldControl(uiSize);
		const helperVisible = Boolean(errorText);
		const controlStyle = {
			width: "100%",
			fontSize: controlSize === "sm" ? "0.8rem" : "0.875rem",
			paddingRight: endAdornment ? 38 : undefined,
		};

		return (
			<Box width={width} mt="1rem" mb="0.5rem">
				<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
					<Field.Label htmlFor={id}>
						{label}
						{required ? <Field.RequiredIndicator /> : null}
					</Field.Label>
					<div style={{ position: "relative", width: "100%" }}>
						{/* Keep the existing raw numeric-string onChange contract by wiring the
						    formatter directly while the v2 Field shell owns label/helper layout. */}
						<NumericFormatAdapter
							customInput={Input}
							id={id}
							name={name}
							value={value}
							disabled={disabled}
							size={controlSize}
							variant="subtle"
							aria-invalid={invalid ? "true" : undefined}
							aria-describedby={helpId}
							onChange={onChange}
							onBlur={onBlur}
							formatSpec={formatSpec}
							style={controlStyle}
						/>
						{endAdornment ? (
							<span
								style={{
									position: "absolute",
									right: 8,
									top: "50%",
									transform: "translateY(-50%)",
									display: "inline-flex",
									alignItems: "center",
									pointerEvents: "auto",
								}}
							>
								{endAdornment}
							</span>
						) : null}
					</div>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: invalid ? mColor.Danger : "inherit",
								visibility: helperVisible ? "visible" : "hidden",
							}}
						>
							{helperVisible ? errorText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};

	return (
		<div>
			{currBaseline && (
				<Stack>
					{/* Name */}
					{renderTextField({
						id: "name",
						name: "name",
						label: "Baseline Name",
						width: "80%",
						required: true,
						value: currBaseline?.name ?? "",
						maxLength: 64,
						onChange: handleInputChange,
						disabled: isDisabled,
						errorText: nameProps.helperText,
					})}

					{/* Description */}
					{renderTextField({
						id: "description",
						name: "description",
						label: "Description",
						width: "80%",
						multiline: true,
						rows: 4,
						value: currBaseline?.description ?? "",
						onChange: handleInputChange,
						disabled: isDisabled,
					})}

					{/* Currency, Unit */}
					{renderSingleSelectField({
						id: "currency_id",
						name: "currency_id",
						required: true,
						label: "Currency Symbol",
						collection: currencyCollection,
						selectedItem: currBaseline?.currency_id ?? "",
						placeholder: "Select a currency symbol...",
						errorText: currencyProps.helperText,
						disabled: isDisabled,
					})}

					{renderSingleSelectField({
						id: "currency_unit",
						name: "currency_unit",
						required: true,
						label: "Currency Unit",
						collection: currencyUnitCollection,
						selectedItem: currBaseline?.currency_unit ?? "",
						placeholder: "Select a currency unit...",
						errorText: currencyUnitProps.helperText,
						disabled: isDisabled,
					})}

					{/* Currency decimal places */}
					{renderNumericField({
						id: "currency_display_decimal",
						name: "currency_display_decimal",
						label: "Number of Decimals to Display",
						width: "60%",
						required: true,
						value: currBaseline.currency_display_decimal,
						onChange: handleInputChange,
						disabled: isDisabled,
						errorText: currencyDisplayProps.helperText,
						endAdornment: (
							<MetreaTooltip
								content="Number of decimal points for the currency display."
							>
								<Icon as={InfoRegular} boxSize="24px" color={mColor.StonyBlue} />
							</MetreaTooltip>
						),
						formatSpec: {
							kind: "number",
							decimals: 0,
							fixed: false,
							allowNegative: false,
						},
					})}

					{/* Number of periods */}
					{renderNumericField({
						id: "number_of_period",
						name: "number_of_period",
						label: "Number of Periods in Baseline",
						width: "60%",
						required: true,
						value: currBaseline.number_of_period,
						onChange: handleInputChange,
						onBlur: verifyInputChange,
						// Number of period(s) can't be changed if there are any associated scenarios.
						disabled: isDisabled || (currBaseline.scenarios && currBaseline.scenarios.length > 0),
						errorText: periodNumberProps.helperText,
						endAdornment: (
							<MetreaTooltip
								content="The number of periods in a baseline cannot be changed once there are any scenarios built that use the baseline. If you need to change the number of periods, copy this baseline into a new baseline and then make the change."
							>
								<Icon as={InfoRegular} boxSize="24px" color={mColor.StonyBlue} />
							</MetreaTooltip>
						),
						formatSpec: {
							kind: "number",
							symbol: "",
							unit: "",
							decimals: 0,
							fixed: false,
							allowNegative: false,
						},
					})}
				</Stack>
			)}
		</div>
	);
};

export default BaselineBasicInfo;
